import type { ServerConnection } from "@/context/server"
import { abortable } from "@/utils/abortable"

export type StreamFailure = "sse-error" | "sse-stall" | "pty-error"
// A stream must survive one normal health-poll interval before brief opens erase failures.
export const streamStabilityMs = 30_000

export function createStreamRecovery(input: {
  current: () => ServerConnection.HttpBase | undefined
  now?: () => number
}) {
  const now = input.now ?? (() => performance.now())
  let failedURL: string | undefined
  let failures = 0
  let generation = 0
  let pending: AbortController | undefined
  let disposed = false
  let pageHidden = false
  const reset = () => {
    generation++
    failures = 0
    failedURL = undefined
    pending?.abort()
    pending = undefined
  }
  const pageHide = () => { pageHidden = true; reset() }
  const pageShow = () => { pageHidden = false; reset() }
  // A backgrounded mobile app can fire pagehide and resume without pageshow.
  const visibilityChange = () => {
    if (document.visibilityState !== "hidden") pageHidden = false
    reset()
  }
  if (typeof window !== "undefined") {
    window.addEventListener("pagehide", pageHide)
    window.addEventListener("pageshow", pageShow)
    window.addEventListener("offline", reset)
    window.addEventListener("online", reset)
    document.addEventListener("visibilitychange", visibilityChange)
  }
  return {
    reset,
    begin(current: ServerConnection.HttpBase | undefined) {
      const server = current ? { ...current } : undefined
      const revision = generation
      let openedAt: number | undefined
      let activeAt: number | undefined
      let finished = false
      // Healthy service is time spent delivering. A stream that reports activity
      // is healthy only until its last delivery, so one that opens and then goes
      // silent is never stable, however long its stall timeout.
      const stable = () => openedAt !== undefined && (activeAt ?? now()) - openedAt >= streamStabilityMs
      return {
        stable,
        opened() { openedAt ??= now() },
        /** The stream delivered an event or heartbeat. */
        active() { activeAt = now() },
        async failed(trigger: StreamFailure) {
          if (finished) return
          finished = true
          if ((typeof document !== "undefined" && document.visibilityState === "hidden") || (typeof navigator !== "undefined" && !navigator.onLine)) return
          if (disposed || pageHidden || revision !== generation || !server?.recover || input.current()?.url !== server.url) return
          if (failedURL !== server.url || stable()) failures = 0
          failedURL = server.url
          // One interruption reconnects normally; repeated short-lived failures try
          // another route. A stall already shows the route stopped delivering.
          if ((++failures < 2 && trigger !== "sse-stall") || pending) return
          failures = 0
          const controller = new AbortController()
          pending = controller
          try {
            await abortable(server.recover(trigger, controller.signal), controller.signal)
          } catch {
            // The existing stream owner retains its bounded reconnect/backoff policy.
          } finally {
            if (pending === controller) pending = undefined
          }
        },
      }
    },
    dispose() {
      disposed = true
      reset()
      if (typeof window !== "undefined") {
        window.removeEventListener("pagehide", pageHide)
        window.removeEventListener("pageshow", pageShow)
        window.removeEventListener("offline", reset)
        window.removeEventListener("online", reset)
        document.removeEventListener("visibilitychange", visibilityChange)
      }
    },
  }
}
